import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Crown, Gift, KeyRound, Loader2, UserRound } from "lucide-react";
import { InviteCard } from "@/components/invite/InviteCard";
import { AccountPlan } from "@/components/billing/AccountPlan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Card = ({ icon: Icon, title, children, testid }) => (
  <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-[#FBBF24]" /><h2 className="font-display text-2xl text-[#F3EBDD]">{title}</h2></div>
    <div className="mt-5">{children}</div>
  </section>
);

function NameForm() {
  const { user, reload } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/auth/me", { name }); await reload(); toast.success("Name updated"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap items-end gap-3">
      <div className="min-w-[240px] flex-1 space-y-2"><Label htmlFor="acc-name">Display name</Label>
        <Input id="acc-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" /></div>
      <Button type="submit" disabled={busy || !name.trim() || name.trim() === user?.name} data-testid="account-name-save">Save name</Button>
      <p className="w-full text-xs text-[#A8A090]">Signed in as <span className="text-[#F3EBDD]" data-testid="account-email">{user?.email}</span></p>
    </form>
  );
}

function PasswordForm() {
  const { signIn } = useAuth();
  const [step, setStep] = useState("form");
  const [f, setF] = useState({ current: "", next: "", confirm: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const start = async (e) => {
    e?.preventDefault();
    if (f.next !== f.confirm) return setError("The new passwords don't match");
    setBusy(true); setError("");
    try { await api.post("/auth/change-password/start", { current_password: f.current, new_password: f.next }); setStep("code"); toast.success("We emailed you a code to confirm the change"); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await signIn("/auth/change-password/confirm", { code: f.code });
      toast.success("Password changed. Other devices have been signed out.");
      setF({ current: "", next: "", confirm: "", code: "" }); setStep("form");
    } catch (err) { setError(errMsg(err)); setF({ ...f, code: "" }); } finally { setBusy(false); }
  };

  if (step === "code") return (
    <form onSubmit={confirm} className="space-y-5" data-testid="password-code-step">
      <p className="text-sm text-[#A8A090]">Enter the 6-digit code we emailed you. Your password won't change until you do.</p>
      <InputOTP maxLength={6} value={f.code} onChange={(v) => setF({ ...f, code: v.replace(/\D/g, "") })} data-testid="password-code-input" inputMode="numeric" autoFocus>
        <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
      </InputOTP>
      {error && <p className="text-sm text-[#F87171]" data-testid="password-error">{error}</p>}
      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" disabled={busy || f.code.length !== 6} data-testid="password-confirm-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm new password</Button>
        <button type="button" onClick={start} disabled={busy} className="text-sm text-[#F59E0B] hover:underline" data-testid="password-resend-button">Send a new code</button>
        <button type="button" onClick={() => { setStep("form"); setError(""); }} className="text-sm text-[#A8A090] hover:text-[#F3EBDD]" data-testid="password-cancel-button">Cancel</button>
      </div>
    </form>
  );
  return (
    <form onSubmit={start} className="grid gap-4 sm:max-w-md" data-testid="password-form">
      <div className="space-y-2"><Label htmlFor="pw-cur">Current password</Label><Input id="pw-cur" type="password" required value={f.current} onChange={set("current")} autoComplete="current-password" data-testid="password-current-input" /></div>
      <div className="space-y-2"><Label htmlFor="pw-new">New password</Label><Input id="pw-new" type="password" required minLength={6} value={f.next} onChange={set("next")} autoComplete="new-password" data-testid="password-new-input" /></div>
      <div className="space-y-2"><Label htmlFor="pw-new2">Confirm new password</Label><Input id="pw-new2" type="password" required minLength={6} value={f.confirm} onChange={set("confirm")} autoComplete="new-password" data-testid="password-confirm-input" /></div>
      {error && <p className="text-sm text-[#F87171]" data-testid="password-error">{error}</p>}
      <div><Button type="submit" disabled={busy} data-testid="password-start-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change password</Button></div>
      <p className="text-xs text-[#A8A090]">We'll email you a code to confirm. When it's confirmed, every other device is signed out.</p>
    </form>
  );
}

export default function AccountPage() {
  return (
    <div className="min-h-screen lf-glow lf-grain">
      <div className="relative z-10 mx-auto max-w-3xl px-6 py-10 sm:px-10">
        <Link to="/" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="account-back-link"><ChevronLeft className="h-3.5 w-3.5" />All universes</Link>
        <div className="lf-label mb-3 mt-12">Account</div>
        <h1 className="font-display text-4xl tracking-tight text-[#F3EBDD] sm:text-5xl">Your account</h1>
        <div className="mt-10 space-y-6">
          <Card icon={UserRound} title="Profile" testid="account-profile"><NameForm /></Card>
          <Card icon={KeyRound} title="Password" testid="account-password"><PasswordForm /></Card>
          <Card icon={Crown} title="Plan & billing" testid="account-billing"><AccountPlan /></Card>
          <div id="invite" className="scroll-mt-6"><Card icon={Gift} title="Invite friends" testid="account-invite"><InviteCard /></Card></div>
        </div>
      </div>
    </div>
  );
}
